import { motion } from "framer-motion";
import {
    HeartHandshake,
    Globe2,
    Newspaper,
    Users,
    Eye,
    Sparkles,
    TrendingUp,
    ArrowUpRight,
    Heart,
} from "lucide-react";

/**
 * Iter-27.73 — Dashboard d'accueil du compte asso avec KPI cards + timeline d'activité.
 *
 * Layout :
 *  - Bandeau de bienvenue + last login indicator
 *  - 4 stat cards principales (Remerciements, Histoires, Actualités, Ambassadeurs)
 *  - Section "Engagement" : total réactions + breakdown emoji + vues stories 30j
 *  - Timeline activité récente (qui a réagi à quoi)
 */
export default function AssoDashboard({ stats, onJumpToTab }) {
    if (!stats) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                    <div
                        key={i}
                        className="h-32 rounded-3xl bg-white/3 border border-white/10 animate-pulse"
                    />
                ))}
            </div>
        );
    }

    const { counts = {}, reactions = {}, story_views_30d = 0, activity_feed = [] } = stats;
    const lastLoginDays = computeDaysAgo(stats.prev_login_at || stats.last_login_at);

    return (
        <div className="space-y-6" data-testid="asso-dashboard">
            {/* Bandeau bienvenue + last login */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl p-5 sm:p-6 bg-gradient-to-r from-shariti-orange/12 via-shariti-yellow/8 to-transparent border border-shariti-orange/25"
            >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-yellow mb-1">
                            Tableau de bord
                        </p>
                        <h1 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">
                            Bienvenue, {stats.name || "partenaire"}
                        </h1>
                        <p className="text-xs sm:text-sm text-white/60 font-light mt-1.5 max-w-2xl">
                            Voici un aperçu de l'impact de tes messages auprès des ambassadeurs cette semaine.
                        </p>
                    </div>
                    {lastLoginDays !== null && (
                        <div
                            className="text-right"
                            data-testid="asso-last-login"
                        >
                            <p className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/40">
                                Dernière connexion
                            </p>
                            <p className="text-sm font-extrabold text-white">
                                {lastLoginDays === 0
                                    ? "Aujourd'hui"
                                    : lastLoginDays === 1
                                      ? "Hier"
                                      : `Il y a ${lastLoginDays} jours`}
                            </p>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* 4 stat cards principales */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <StatCard
                    label="Remerciements"
                    value={counts.gratitude}
                    icon={HeartHandshake}
                    color="#FCDB53"
                    onClick={() => onJumpToTab?.("gratitude")}
                    testId="stat-gratitude"
                />
                <StatCard
                    label="Histoires d'impact"
                    value={counts.impact}
                    icon={Globe2}
                    color="#EE722E"
                    onClick={() => onJumpToTab?.("impact")}
                    testId="stat-impact"
                />
                <StatCard
                    label="Actualités"
                    value={counts.news}
                    icon={Newspaper}
                    color="#7dd3fc"
                    onClick={() => onJumpToTab?.("news")}
                    testId="stat-news"
                />
                <StatCard
                    label="Ambassadeurs"
                    value={counts.ambassadors}
                    icon={Users}
                    color="#a78bfa"
                    onClick={() => onJumpToTab?.("analytics")}
                    testId="stat-ambassadors"
                    accent
                />
            </div>

            {/* Engagement reactions + vues stories */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Total réactions */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="lg:col-span-2 rounded-3xl p-5 sm:p-6 bg-white/3 border border-white/10"
                    data-testid="engagement-card"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/45 mb-0.5">
                                Engagement
                            </p>
                            <h3 className="text-lg font-extrabold text-white flex items-center gap-2">
                                <Heart className="w-4 h-4 text-shariti-orange-light" fill="currentColor" />
                                Réactions reçues
                            </h3>
                        </div>
                        <span className="text-3xl font-extrabold text-white">
                            {reactions.total || 0}
                        </span>
                    </div>
                    {reactions.total > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(reactions.by_emoji || {}).map(([emoji, n]) => (
                                <div
                                    key={emoji}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-white/5 border border-white/10"
                                    data-testid={`reaction-${emoji}`}
                                >
                                    <span className="text-base">{emoji}</span>
                                    <span className="text-sm font-extrabold text-white/85">{n}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-white/45 italic">
                            Aucune réaction pour le moment. Publie un remerciement pour démarrer l'engagement.
                        </p>
                    )}
                </motion.div>

                {/* Vues Stories 30j */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="rounded-3xl p-5 sm:p-6 bg-gradient-to-br from-shariti-yellow/12 to-transparent border border-shariti-yellow/30"
                    data-testid="story-views-card"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Eye className="w-4 h-4 text-shariti-yellow" />
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-yellow">
                            Vues Stories
                        </p>
                    </div>
                    <p className="text-3xl font-extrabold text-white">{story_views_30d}</p>
                    <p className="text-[11px] text-white/55 font-light mt-1">
                        Sur les 30 derniers jours
                    </p>
                </motion.div>
            </div>

            {/* Iter-29 — Section "Activité récente" retirée : elle n'affichait
                que des réactions emoji (issues des tests démo), ce qui polluait
                l'espace partenaire premium. Les réactions restent disponibles
                côté ambassadeur. Le feed sera ré-introduit plus tard avec des
                événements pertinents (lectures, partages, propositions de don). */}
        </div>
    );
}


function StatCard({ label, value, icon: Icon, color, onClick, testId, accent = false }) {
    return (
        <motion.button
            whileHover={{ y: -2 }}
            onClick={onClick}
            data-testid={testId}
            className={`group text-left rounded-3xl p-4 sm:p-5 border transition-all ${
                accent
                    ? "bg-gradient-to-br from-white/5 to-transparent border-white/15"
                    : "bg-white/3 border-white/10"
            } hover:border-white/25 hover:shadow-xl hover:shadow-black/30`}
        >
            <div className="flex items-start justify-between mb-3">
                <div
                    className="w-10 h-10 rounded-2xl flex items-center justify-center"
                    style={{
                        background: `${color}1A`,
                        border: `1px solid ${color}40`,
                    }}
                >
                    <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-white/70 transition-colors" />
            </div>
            <p className="text-2xl sm:text-3xl font-extrabold text-white tabular-nums tracking-tight">
                {value ?? 0}
            </p>
            <p className="text-[11px] sm:text-xs font-bold text-white/55 mt-0.5 uppercase tracking-wider">
                {label}
            </p>
        </motion.button>
    );
}


function ActivityRow({ a }) {
    const when = formatRelative(a.at);
    return (
        <li className="flex items-start gap-3 p-3 rounded-2xl bg-white/3 border border-white/8">
            {a.user_avatar ? (
                <img
                    src={a.user_avatar}
                    alt={a.user_name}
                    className="w-9 h-9 rounded-full object-cover shrink-0"
                />
            ) : (
                <div className="w-9 h-9 rounded-full bg-shariti-orange/15 flex items-center justify-center shrink-0">
                    <span className="text-xs font-extrabold text-shariti-orange-light">
                        {initialsOf(a.user_name)}
                    </span>
                </div>
            )}
            <div className="flex-1 min-w-0">
                <p className="text-sm text-white/85 leading-snug">
                    <span className="font-bold">{a.user_name}</span>{" "}
                    <span className="text-white/55 font-light">a réagi avec </span>
                    <span className="text-lg align-middle">{a.emoji}</span>
                    <span className="text-white/55 font-light"> à </span>
                    <span className="font-bold">"{a.news_title}"</span>
                </p>
                <p className="text-[10px] text-white/40 font-light mt-0.5 uppercase tracking-wider">
                    {when}
                </p>
            </div>
        </li>
    );
}


function initialsOf(name) {
    if (!name) return "?";
    return name
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() || "")
        .join("");
}


function computeDaysAgo(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const diff = Date.now() - d.getTime();
        return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
    } catch {
        return null;
    }
}


function formatRelative(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = Date.now() - d.getTime();
        const min = Math.floor(diff / 60000);
        if (min < 1) return "à l'instant";
        if (min < 60) return `il y a ${min} min`;
        const h = Math.floor(min / 60);
        if (h < 24) return `il y a ${h}h`;
        const days = Math.floor(h / 24);
        if (days === 1) return "hier";
        return `il y a ${days} jours`;
    } catch {
        return "";
    }
}
